import { useEffect, useRef, useState } from "react"
import ThemeModal from "components/Common/ThemeModal"
import { Button, ButtonGroup, Form } from "reactstrap"
import {
  createEmptyLoanErrors,
  LoanFormatPreview,
  LoanInformationFormFields,
  LoanInformationMetrics,
  LoanInformationSummary,
  normalizeLoanPayload,
  validateLoanFormValues,
} from "pages/MoneyReceiptPages/MoneyReceiptLoanShared"
import { useAccountBankAccounts } from "hooks/useAccountBankAccount"
import { useAccountPaymentMethods } from "hooks/useAccountPaymentMethod"
import { useEmployees } from "hooks/employeeHook"

export const SUPPORTIVE_DOCUMENT_ACTIONS = [
  { key: "bill", label: "Bill" },
  { key: "delivery-challan", label: "Delivery Challan" },
  { key: "money-receipt-customer", label: "Money Receipt (Customer)" },
  { key: "money-receipt-bank", label: "Money Receipt (Bank)", loanOnly: true },
]

const SupportiveLoanAddEdit = ({
  modal,
  toggleModal,
  supportive,
  initialValues,
  onSubmit,
  isSubmitting = false,
  activeDocumentKey = null,
  onDocumentDownload,
}) => {
  const [type, setType] = useState(initialValues?.type || "company")
  const [values, setValues] = useState(initialValues)
  const [errors, setErrors] = useState(createEmptyLoanErrors())

  const { data: bankAccountsResponse } = useAccountBankAccounts(
    { all: true, is_active: true },
    modal
  )
  const bankAccounts =
    bankAccountsResponse?.data?.data ?? bankAccountsResponse?.data ?? []

  const { data: paymentMethodsResponse } = useAccountPaymentMethods(
    { all: true, per_page: 200 },
    modal
  )
  const paymentMethods =
    paymentMethodsResponse?.data?.data ?? paymentMethodsResponse?.data ?? []

  const { data: employeesResponse } = useEmployees(
    { all: true, per_page: 500 },
    modal
  )
  const employees =
    employeesResponse?.data?.data ?? employeesResponse?.data ?? []

  const initialValuesRef = useRef(initialValues)
  initialValuesRef.current = initialValues

  useEffect(() => {
    if (modal) {
      setType(initialValuesRef.current?.type || "company")
      setValues(initialValuesRef.current)
      setErrors(createEmptyLoanErrors())
    }
  }, [modal])

  const handleChange = event => {
    const { name, value } = event.target
    setValues(current => ({
      ...current,
      [name]: value,
    }))
    setErrors(current => ({
      ...current,
      [name]: "",
    }))
  }

  const handleSubmit = async event => {
    event.preventDefault()

    if (isSubmitting) {
      return
    }

    const nextErrors = validateLoanFormValues(values)
    setErrors(nextErrors)

    if (Object.values(nextErrors).some(Boolean)) {
      return
    }

    const payload = {
      type,
      loan_information: normalizeLoanPayload(values),
    }

    try {
      await onSubmit(payload)
    } catch (error) {
      // keep modal open so user can retry; parent shows toast
    }
  }

  const title = supportive?.sqid
    ? `Loan Info & Documents - ${supportive.sqid}`
    : "Loan Info & Documents"

  const handleToggle = () => {
    if (isSubmitting) {
      return
    }
    toggleModal()
  }

  const visibleDocuments = SUPPORTIVE_DOCUMENT_ACTIONS.filter(
    action => !action.loanOnly || type === "loan"
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={handleToggle}
      title={title}
      size="lg"
      modalBodyClass="bg-body-tertiary"
      footer={
        <div className="d-flex justify-content-end gap-2 w-100">
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      }
    >
      <div className="rounded-3 border bg-white p-3 mb-4">
        <div className="text-uppercase text-muted small fw-semibold mb-2">
          Document Type
        </div>
        <ButtonGroup size="sm" className="d-flex">
          <Button
            type="button"
            color={type === "company" ? "primary" : "light"}
            outline={type !== "company"}
            onClick={() => setType("company")}
            disabled={isSubmitting}
          >
            Company / Direct
          </Button>
          <Button
            type="button"
            color={type === "loan" ? "primary" : "light"}
            outline={type !== "loan"}
            onClick={() => setType("loan")}
            disabled={isSubmitting}
          >
            Bank Loan
          </Button>
        </ButtonGroup>
      </div>

      <Form onSubmit={handleSubmit}>
        {type === "loan" && <LoanInformationSummary loanInformation={values} />}
        <div className="rounded-3 border bg-white p-3 mb-4">
          <LoanInformationFormFields
            values={values}
            errors={errors}
            onChange={handleChange}
            disabled={isSubmitting}
            bankAccounts={bankAccounts}
            paymentMethods={paymentMethods}
            employees={employees}
            showLoanSections={type === "loan"}
          />
        </div>
        {type === "loan" && (
          <>
            <LoanFormatPreview loanInformation={values} className="mb-4" />
            <LoanInformationMetrics loanInformation={values} />
          </>
        )}
      </Form>

      <div className="rounded-3 border bg-white p-3 mt-4">
        <div className="text-uppercase text-muted small fw-semibold mb-2">
          Documents
        </div>
        <div className="d-flex flex-wrap gap-2">
          {visibleDocuments.map(action => (
            <Button
              key={action.key}
              color="light"
              size="sm"
              onClick={() => onDocumentDownload?.(action.key)}
              disabled={activeDocumentKey !== null}
            >
              <i
                className={`bx ${
                  activeDocumentKey === action.key
                    ? "bx-loader bx-spin"
                    : "bx-printer"
                } me-1`}
              />
              {action.label}
            </Button>
          ))}
        </div>
      </div>
    </ThemeModal>
  )
}

export default SupportiveLoanAddEdit
